import { useMemo } from 'react';

import { parseFlightDatesAndProcuredOfferDates } from '@/v2/services/sanity/utils/parser/campaign/parseCampaign';
import { UI } from '@/v2/types';

export type UseCampaignMessageOptions = {
  campaign: Pick<UI.Campaign, 'calendar' | 'flights'> | undefined | null;
  originCode: string | undefined;
  destinationCode: string | undefined;
  procuredOffer: Pick<UI.ProcuredOffer, 'travelDateRange'> | undefined | null;
};

export type UseCampaignMessageReturn = {
  description: string;
  title: string;
} | null;

export const useCampaignMessage = ({
  campaign,
  originCode,
  destinationCode,
  procuredOffer,
}: UseCampaignMessageOptions): UseCampaignMessageReturn => {
  const selectedRoute = useMemo(
    () =>
      campaign?.calendar?.routes?.find(
        (r) => r.route === `${originCode}${destinationCode}`,
      ),
    [campaign?.calendar?.routes, destinationCode, originCode],
  );

  const overlappingCampaignAndProcuredOfferDatesMessage = useMemo(() => {
    if (!selectedRoute || !procuredOffer || !campaign) {
      return null;
    }

    return parseFlightDatesAndProcuredOfferDates(
      procuredOffer,
      selectedRoute,
      campaign,
    );
  }, [campaign, procuredOffer, selectedRoute]);

  const description = useMemo(() => {
    if (overlappingCampaignAndProcuredOfferDatesMessage) {
      return overlappingCampaignAndProcuredOfferDatesMessage;
    }

    if (selectedRoute) {
      return selectedRoute.message;
    }

    if (originCode) {
      return campaign?.calendar?.unsupportedRouteMessage;
    }

    return campaign?.calendar?.noOriginMessage;
  }, [
    campaign?.calendar?.noOriginMessage,
    campaign?.calendar?.unsupportedRouteMessage,
    overlappingCampaignAndProcuredOfferDatesMessage,
    originCode,
    selectedRoute,
  ]);

  const title = campaign?.calendar?.title;

  return title && description ? { title, description } : null;
};
